import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { SOPO_DIALOG_DATA, SopoDialogRef, SopoDialogSettings } from '../../interfaces/dialog';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';

export class SopoVerifyDataSettings extends SopoDialogSettings {
    data: SopoVerifyDataInput;
}
export interface SopoVerifyDataInput {
    header?: string;
    body?: string;
    okText?: string;
    cancelText?: string;
    phone: string;
    birthDate?: string;
    firstName?: string;
    middleName?: string;
    lastName?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
    ssn?: string;
    isEditable?: boolean;
}

@Component({
    selector: 'app-verify-info',
    templateUrl: './sopo-verify-info.component.html',
    styleUrls: ['./sopo-verify-info.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class SopoVerifyInfoPopupComponent implements OnInit {
    header = 'Verify Your Information';
    body = 'Please carefully review the information below and verify if everything is correct. If it isn’t accurate, please press "Edit" below.';
    birthDate: string = '';
    
    isEditable = false;
    ageError = false;
    missingFieldsError = false;
    ssnError = false;
    countryList;
    submitted = false;

    form: SopoVerifyDataInput;

    constructor(
        @Inject(SOPO_DIALOG_DATA) data: SopoVerifyDataInput,
        private dialogRef: SopoDialogRef,
        private ref: ChangeDetectorRef,
    ) {
        this.form = { ...data };
        if (data.header) this.header = data.header;
        if (data.body) this.body = data.body;
        if (data.isEditable) this.isEditable = data.isEditable;
        if (!this.form.birthDate) this.form.birthDate = new Date().toISOString();
        if (!this.form.middleName) this.form.middleName = '';
        this.formatPhoneNumber();
        this.initCountryList();
    }

    initCountryList() {
        countries.registerLocale(enLocale);
        this.countryList = [
            { code: 'US', name: 'United States' },
            ...Object.entries(countries.getNames('en'))
            .filter(([code]) => code !== 'US')
            .map(([code, name]) => ({ code, name }))
        ];
    }

    ngOnInit(): void { }

    async confirm() {
        this.submitted = true;
        this.validateForm();

        if (!this.ageError && !this.missingFieldsError && !this.ssnError) {
            this.dialogRef.close({
                verifiedData: this.form,
                isEdited: this.isEditable
            });
        }
    }

    validateForm() {
        if (!this.submitted) return;
        this.ageError = !this.isAboveLegalAge();
        this.ssnError = !(this.form.ssn && /^\d{9}$/.test(this.form.ssn));
        this.missingFieldsError = this.formHasMissingFields();
        this.ref.detectChanges();
    }

    isAboveLegalAge() {
        const birthDate = new Date(this.form.birthDate);
        const today = new Date();

        const age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        const dayDiff = today.getDate() - birthDate.getDate();

        const isAbove18 = age > 18 || (age === 18 && (monthDiff > 0 || (monthDiff === 0 && dayDiff >= 0)));
        return isAbove18;
    }

    formHasMissingFields(): boolean {
        const {
            firstName,
            lastName,
            addressLine1,
            city,
            state,
            zip,
            country,
            birthDate,
            ssn
        } = this.form;

        return !firstName || !lastName || !addressLine1 || !city || !state || !zip || !country || !birthDate || !ssn;
    }

    incorrectInfo() {
        this.isEditable = true;
        this.form.ssn = '';
        this.ref.detectChanges();
    }

    cancel() {
        this.dialogRef.close(null);
    }

    formatPhoneNumber() {
        try {
            const raw = this.form.phone.replace(/\D/g, '');
            if (raw.length === 11 && raw.startsWith("1")) {
                this.form.phone = `(${raw.slice(1, 4)})-${raw.slice(4, 7)}-${raw.slice(7)}`;
            } else if (raw.length === 10) {
                this.form.phone = `(${raw.slice(0, 3)})-${raw.slice(3, 6)}-${raw.slice(6)}`;
            }
        } catch (error) {
            console.error('[VerifyInfoPopup] format phone number: ', error);
        }
    }

    getMaskedSSN(ssn: string): string {
        if (!ssn || ssn.length !== 9) return '';
        return `•••-••-${ssn.slice(-4)}`;
    }
}
